/// <amd-module name="VSSUI/Components/HubHeader/HubHeader" />

import * as React from "react";
import { IBreadCrumbData } from "OfficeFabric/Breadcrumb";
import { BaseComponent } from "OfficeFabric/Utilities";
import { css } from "VSSUI/Util";
import { IVssBreadcrumb, IVssBreadcrumbItemInternal } from "../../Components/VssBreadcrumb/VssBreadcrumb.Props";
import { VssBreadcrumb } from "../../Components/VssBreadcrumb/VssBreadcrumb";
import { VssBreadcrumbStyles } from "../../Components/VssBreadcrumb/VssBreadcrumb.Props";
import { HubTitle } from "../../Components/HubHeader/HubTitle";
import { IHubHeaderProps } from "../../Components/HubHeader/HubHeader.Props";
import { IItemPickerProvider, IPickListSelection } from "VSSUI/PickList";
import { IObservableValue, ObservableLike } from "VSS/Core/Observable";

import "VSS/LoaderPlugins/Css!VSSUI/Components/HubHeader/HubHeader";

const MAX_BREADCRUMB_WIDTH = "340px";

/** @deprecated */
export interface IHubHeaderState {
    headerItemPicker?: IItemPickerProvider;
    canBreadcrumbShrink: boolean;
}

/**
 * Component that generates a standard VSTS page header.
 */
/** @deprecated */
export class HubHeader extends BaseComponent<IHubHeaderProps, IHubHeaderState> {
    private _hubBreadcrumb: IVssBreadcrumb;

    constructor(props: IHubHeaderProps) {
        super(props);

        this.state = {
            headerItemPicker: ObservableLike.getValue(this.props.headerItemPicker),
            canBreadcrumbShrink: true
        };
    }

    public componentWillMount(): void {
        ObservableLike.subscribe(this.props.headerItemPicker, this.onHeaderPickerChanged);
    }

    public componentWillUnmount(): void {
        ObservableLike.unsubscribe(this.props.headerItemPicker, this.onHeaderPickerChanged);
    }

    public componentWillReceiveProps(nextProps: IHubHeaderProps) {
        if (this.props.headerItemPicker !== nextProps.headerItemPicker) {
            this.setState({ headerItemPicker: ObservableLike.getValue(nextProps.headerItemPicker) });
            ObservableLike.unsubscribe(this.props.headerItemPicker, this.onHeaderPickerChanged);
            ObservableLike.subscribe(nextProps.headerItemPicker, this.onHeaderPickerChanged);
        }
    }

    /**
     * Gets the title (Breadcrumb control or HubTitle control, depending on whether or not breadcrumbitems were passed).
     */
    private getTitle(): JSX.Element | null {
        if ((this.props.breadcrumbItems && this.props.breadcrumbItems.length > 0) || this.props.headerItemPicker) {
            return this.getBreadcrumb();
        } else {
            if (this.props.title) {
                return <HubTitle titleText={this.props.title} iconProps={this.props.iconProps} />;
            } else {
                console.log("Must use title or breadcrumb items + headerItemPicker");
                return null;
            }
        }
    }

    private getBreadcrumb(): JSX.Element {
        let breadcrumbItems: IVssBreadcrumbItemInternal[] = this.props.breadcrumbItems ? [...this.props.breadcrumbItems] : [];
        const {
            maxBreadcrumbItemWidth,
            collapseBreadcrumb,
            iconProps,
            pickListClassName,
            pickListMinWidth,
            pickListMaxWidth,
            pickListMaxHeight,
            title,
            hubBreadcrumbAriaLabel,
            breadcrumbProps
        } = this.props;

        const { headerItemPicker } = this.state;

        if (headerItemPicker && headerItemPicker.getListItem) {
            const selectedItem = headerItemPicker.selectedItem && headerItemPicker.getListItem(headerItemPicker.selectedItem);
            const selectedItemTitleText =
                headerItemPicker.selectedItem && headerItemPicker.getTitleTextForItem
                    ? headerItemPicker.getTitleTextForItem(headerItemPicker.selectedItem)
                    : selectedItem && selectedItem.name;
            const titleItem: IVssBreadcrumbItemInternal = {
                key: "title-item",
                text: title ? title : selectedItemTitleText,
                leftIconProps: iconProps,
                onSelectionChanged: this.onSelectionChanged,
                itemPicker: headerItemPicker,
                ariaLabel: headerItemPicker.ariaLabel,
                isHeaderPickerItem: true
            };

            breadcrumbItems.push(titleItem);
        } else if (title) {
            const titleItem: IVssBreadcrumbItemInternal = {
                key: "title-item",
                text: title,
                leftIconProps: iconProps
            };

            breadcrumbItems.push(titleItem);
        }

        if (collapseBreadcrumb) {
            breadcrumbItems = breadcrumbItems.slice(breadcrumbItems.length - 1);
            // If breadcrumbProps is passed, make sure overflowIndex is 0 when collapseBreadcrumb
            if (breadcrumbProps) {
                breadcrumbProps.overflowIndex = 0;
            }
        }

        return (
            <VssBreadcrumb
                {...breadcrumbProps}
                dataDidRender={this.dataDidRender}
                ref={hubBreadcrumb => {
                    this._hubBreadcrumb = hubBreadcrumb;
                }}
                items={breadcrumbItems}
                maxItemWidth={typeof maxBreadcrumbItemWidth === "string" ? maxBreadcrumbItemWidth : MAX_BREADCRUMB_WIDTH}
                pickListClassName={pickListClassName}
                pickListMinWidth={pickListMinWidth}
                pickListMaxWidth={pickListMaxWidth}
                pickListMaxHeight={pickListMaxHeight}
                ariaLabel={hubBreadcrumbAriaLabel}
                shouldUseFocusZone
                presentationStyles={VssBreadcrumbStyles.dividerLink | VssBreadcrumbStyles.minHeight}
            />
        );
    }

    private dataDidRender = (renderedData: IBreadCrumbData, itemsWidth?: number): void => {
        const { breadcrumbProps, breadcrumbDataDidRender } = this.props;
        const { canBreadcrumbShrink } = this.state;

        if (breadcrumbProps && breadcrumbProps.onReduceData) {
            const breadcrumbData = breadcrumbProps.onReduceData(renderedData);
            if (breadcrumbData === undefined && canBreadcrumbShrink) {
                this.setState({ canBreadcrumbShrink: false });
            } else if (breadcrumbData !== undefined && !canBreadcrumbShrink) {
                this.setState({ canBreadcrumbShrink: true });
            }
        }

        if (breadcrumbDataDidRender) {
            breadcrumbDataDidRender(renderedData, itemsWidth);
        }
    };

    private onSelectionChanged = (selection: IPickListSelection): boolean => {
        const { headerItemPicker } = this.state;
        let preventDefault = false;

        if (headerItemPicker && headerItemPicker.onSelectedItemChanged) {
            preventDefault =
                headerItemPicker.onSelectedItemChanged(
                    selection.selectedItems && selection.selectedItems.length > 0 ? selection.selectedItems[0] : null
                ) === true;
            if (this._hubBreadcrumb && this._hubBreadcrumb.pickList) {
                this._hubBreadcrumb.pickList.closeDropdown();
            }
        }

        return preventDefault;
    };

    private onHeaderPickerChanged = (headerItemPicker: IItemPickerProvider): void => {
        this.setState({ headerItemPicker });
    };

    public render(): JSX.Element {
        return (
            <div className={css("vss-HubHeader", !this.state.canBreadcrumbShrink && "vss-HubHeader-no-shrink")}>
                {this.getTitle()}
                {this.props.children}
            </div>
        );
    }
}
